// components/ClientCopyPrepPanel.jsx — Pre-Copy Client: temporarily raise Sybase ASE
// parameters before a large client copy / client delete, then roll back.
// Backend: /api/sap/client-copy-prep/*  (backend/clientCopyPrep.js)
import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Gauge, Undo2, Play } from 'lucide-react';
import { api as axiosApi } from '../context/AuthContext';

const GB = 524288;
const fmt = (n) => (n === null || n === undefined || n === '' ? '—' : Number(n).toLocaleString());
const fmtVal = (name, v) => (v === undefined || v === null ? '—'
  : name === 'max memory' ? `${fmt(v)} (${(v / GB).toFixed(1)} GB)` : fmt(v));
const fmtMB = (mb) => (mb === undefined || mb === null ? '—' : mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb} MB`);

async function call(method, url, body) {
  try {
    const r = method === 'get' ? await axiosApi.get(url) : await axiosApi.post(url, body);
    return r.data;
  } catch (e) { throw new Error(e.response?.data?.error || e.message); }
}

const RUNBOOK = [
  'Export client P01-310 (SCC8) — note the transport requests created',
  'Stop SAP + ASE Q03 (and Q02 if needed) to free host memory',
  'Refresh this panel, choose a profile and click Apply Pre-Copy Settings (ASE tuning, log safeguards, suspend jobs)',
  'Restart ASE Q01 if the panel asks (engines / descriptors)',
  'Delete client 230 (SCC5) and re-create it (SCC4) — optional, see notes',
  'Import the client transport into Q01-230 (STMS / Transport page) with parallel processing',
  'Run post-import processing (SCC7) and wait until it finishes',
  'Click Rollback to Original Settings (restores ASE + runs BTCTRNS2)',
  'Restart ASE Q01 if the panel asks',
  'Take a full database dump of Q01',
  'Start Q02 / Q03 again',
];
const RB_KEY = 'ccp.runbook.v1';

function Runbook() {
  const [done, setDone] = useState(() => { try { return JSON.parse(localStorage.getItem(RB_KEY) || '{}'); } catch (_) { return {}; } });
  const toggle = (i) => setDone(d => { const n = { ...d, [i]: !d[i] }; try { localStorage.setItem(RB_KEY, JSON.stringify(n)); } catch (_) {} return n; });
  const reset = () => { setDone({}); try { localStorage.removeItem(RB_KEY); } catch (_) {} };
  const count = RUNBOOK.filter((_, i) => done[i]).length;
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <b style={{ fontSize: 13, color: '#1e293b' }}>Work sequence — client refresh P01-310 → Q01-230 ({count}/{RUNBOOK.length})</b>
        <button onClick={reset} style={{ border: '1px solid #cbd5e1', background: '#fff', borderRadius: 6, padding: '2px 10px', fontSize: 12, cursor: 'pointer', color: '#475569' }}>Reset</button>
      </div>
      <ol style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
        {RUNBOOK.map((txt, i) => (
          <li key={i} onClick={() => toggle(i)} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '3px 0', fontSize: 13, cursor: 'pointer', color: done[i] ? '#94a3b8' : '#334155', textDecoration: done[i] ? 'line-through' : 'none' }}>
            <input type="checkbox" readOnly checked={!!done[i]} style={{ marginTop: 3 }} />
            <span><b>{i + 1}.</b> {txt}</span>
          </li>))}
      </ol>
    </div>
  );
}

export default function ClientCopyPrepPanel({ sid = 'Q01' }) {
  const [meta, setMeta] = useState(null);
  const [st, setSt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState(null);
  const [profile, setProfile] = useState('full');
  const [custom, setCustom] = useState({});
  const [suspendJobs, setSuspendJobs] = useState(true);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const [m, s] = await Promise.all([
        meta ? Promise.resolve(meta) : call('get', '/api/sap/client-copy-prep/meta'),
        call('get', `/api/sap/client-copy-prep/status?sid=${encodeURIComponent(sid)}`),
      ]);
      setMeta(m); setSt(s);
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }, [sid, meta]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sid]);

  const params = meta?.params || [];
  const profiles = meta?.profiles || {};
  const target = profile === 'custom' ? custom : (profiles[profile]?.values || {});
  const active = st?.active;

  const doApply = async () => {
    const lines = params.filter(p => target[p.name] !== undefined && target[p.name] !== '')
      .map(p => `  ${p.label}: ${fmtVal(p.name, (st?.values?.[p.name] || {}).cfg)} → ${fmtVal(p.name, Number(target[p.name]))}`).join('\n');
    if (!window.confirm(`Apply Pre-Copy settings to ${sid}?\n\n${lines}${suspendJobs ? '\n  Background jobs: suspend (BTCTRNS1)' : ''}\n\nCurrent values will be saved for rollback.`)) return;
    setBusy('apply'); setMsg(null); setErr('');
    try {
      const body = { sid, profile, suspendJobs };
      if (profile === 'custom') body.values = custom;
      const r = await call('post', '/api/sap/client-copy-prep/apply', body);
      setMsg({ ok: r.ok && !r.failed?.length, title: `Apply: ${r.status}`, failed: r.failed, restart: r.restartPending, output: r.output });
      await load();
    } catch (e) { setErr(e.message); }
    setBusy('');
  };

  const doRollback = async () => {
    if (!window.confirm(`Roll back ${sid} ASE parameters to the values saved on ${active?.applied_at?.slice(0, 16).replace('T', ' ')}?`)) return;
    setBusy('rollback'); setMsg(null); setErr('');
    try {
      const r = await call('post', '/api/sap/client-copy-prep/rollback', { sid });
      setMsg({ ok: r.ok, title: r.ok ? 'Rollback completed' : 'Rollback partially failed', failed: r.failed, restart: r.restartPending, output: r.output });
      await load();
    } catch (e) { setErr(e.message); }
    setBusy('');
  };

  const card = { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px' };
  const th = { background: '#f8fafc', padding: '7px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: '#64748b', borderBottom: '1px solid #e2e8f0', textTransform: 'uppercase', whiteSpace: 'nowrap' };
  const td = { padding: '7px 10px', borderBottom: '1px solid #f1f5f9', fontSize: 13, color: '#1e293b', whiteSpace: 'nowrap' };
  const btn = (bg) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, background: bg, color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer' });

  const others = Object.entries(st?.procs || {}).filter(([s]) => s !== sid);

  return (
    <div style={{ border: '1px solid #c7d2fe', background: '#fafaff', borderRadius: 12, padding: 14, marginBottom: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
        <div style={{ fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Gauge size={16} /> Pre-Copy Client — ASE Tuning <span style={{ color: '#94a3b8', fontWeight: 500, fontSize: 12 }}>({sid})</span>
          {active && <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: 999, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>ACTIVE since {active.applied_at?.slice(0, 16).replace('T', ' ')}</span>}
        </div>
        <button onClick={load} disabled={loading} style={{ ...btn('#64748b'), opacity: loading ? 0.6 : 1 }}>
          <RefreshCw size={14} className={loading ? 'spin' : ''} /> {loading ? 'Checking…' : 'Refresh'}
        </button>
      </div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
        Raises locks, memory and descriptor settings before a large client copy / client delete, and restores the saved values afterwards.
        Stop Q03 (and Q02 if needed) first to free host memory.
      </div>

      <Runbook />

      {err && <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 10 }}>{err}</div>}

      {st && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 10, marginBottom: 14 }}>
          <div style={card}><div style={{ fontSize: 11, color: '#64748b' }}>Host RAM available</div><div style={{ fontWeight: 700 }}>{fmtMB(st.mem?.availMB)} / {fmtMB(st.mem?.totalMB)}</div></div>
          <div style={card}><div style={{ fontSize: 11, color: '#64748b' }}>Swap used</div><div style={{ fontWeight: 700 }}>{fmtMB(st.swap?.usedMB)}</div></div>
          <div style={card}><div style={{ fontSize: 11, color: '#64748b' }}>ASE instances</div>
            <div style={{ fontWeight: 700, fontSize: 13 }}>
              {[[sid, st.procs?.[sid]], ...others].map(([s, up]) => (
                <span key={s} style={{ marginRight: 8, color: up ? '#16a34a' : '#94a3b8' }}>{s} {up ? `● ${fmtMB(st.rssMB?.[s])}` : '○ down'}</span>))}
            </div></div>
          <div style={card}><div style={{ fontSize: 11, color: '#64748b' }}>{sid} data free</div><div style={{ fontWeight: 700 }}>{fmtMB(st.space?.data?.freeMB)} / {fmtMB(st.space?.data?.totalMB)}</div></div>
          <div style={card}><div style={{ fontSize: 11, color: '#64748b' }}>Background jobs</div>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{st.jobs?.error ? <span style={{ color: '#dc2626' }}>n/a</span>
              : <>{fmt(st.jobs?.released)} released · <span style={{ color: st.jobs?.suspended ? '#b45309' : undefined }}>{fmt(st.jobs?.suspended)} suspended</span></>}</div></div>
          <div style={card}><div style={{ fontSize: 11, color: '#64748b' }}>{sid} log free</div><div style={{ fontWeight: 700 }}>{fmtMB(st.space?.log?.freeMB)} / {fmtMB(st.space?.log?.totalMB)}</div></div>
        </div>
      )}

      {st?.extras && (
        <div style={{ ...card, marginBottom: 12, fontSize: 13, color: '#334155' }}>
          <b>Log safeguards</b> — saptools <code>trunc log on chkpt</code>:{' '}
          <b style={{ color: st.extras.saptoolsTrunc ? '#16a34a' : '#64748b' }}>{st.extras.saptoolsTrunc ? 'ON' : 'off'}</b>
          {' · '}{sid} <code>delayed commit</code>:{' '}
          <b style={{ color: st.extras.delayedCommit ? '#16a34a' : '#64748b' }}>{st.extras.delayedCommit ? 'ON' : 'off'}</b>
          {' · '}Data cache: <b>{fmtMB(st.extras.cacheMB)}</b> (128K pool {fmtMB(st.extras.pool128MB)})
          <br />          {' · '}{sid} log thresholds:{' '}
          {(st.extras.thresholds || []).map((x, i) => {
            const pct = st.space?.log?.totalMB ? Math.round(x.pages * 16384 / 1048576 / st.space.log.totalMB * 100) : null;
            return <span key={i} style={{ marginRight: 8, color: x.proc === 'ccp_log_threshold' ? '#4338ca' : '#64748b' }}>
              {fmtMB(Math.round(x.pages * 16384 / 1048576))}{pct !== null ? ` (${pct}%)` : ''} → {x.proc}</span>;
          })}
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
            Apply also enables delayed commit on {sid}, enlarges the default data cache (preset profiles), enables saptools trunc log on chkpt and adds 20% / 10% free-space thresholds on the {sid} log
            (alert via ASE Health Check → Telegram/email). Rollback restores all of them (a smaller data cache takes effect after the next ASE restart). Take a full database dump of {sid} after the copy.
          </div>
        </div>
      )}
      {st?.restartPending?.length > 0 && (
        <div style={{ background: '#fff7ed', color: '#9a3412', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 10 }}>
          ASE restart required for: <b>{st.restartPending.join(', ')}</b> (config value differs from run value).
        </div>
      )}

      {!active && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          {Object.entries(profiles).map(([k, p]) => (
            <button key={k} onClick={() => setProfile(k)} title={p.note}
              style={{ border: '1px solid ' + (profile === k ? '#4f46e5' : '#cbd5e1'), background: profile === k ? '#eef2ff' : '#fff', color: profile === k ? '#3730a3' : '#334155', borderRadius: 8, padding: '6px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              {p.label}</button>))}
          <button onClick={() => setProfile('custom')}
            style={{ border: '1px solid ' + (profile === 'custom' ? '#4f46e5' : '#cbd5e1'), background: profile === 'custom' ? '#eef2ff' : '#fff', color: profile === 'custom' ? '#3730a3' : '#334155', borderRadius: 8, padding: '6px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Custom</button>
        </div>
      )}

      {st && (
        <div style={{ overflowX: 'auto', marginBottom: 12 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={th}>Parameter</th><th style={th}>Run value</th><th style={th}>Config value</th>
              <th style={th}>{active ? 'Saved (rollback to)' : 'Target'}</th>
            </tr></thead>
            <tbody>
              {params.map(p => {
                const v = st.values?.[p.name] || {};
                return (
                  <tr key={p.name}>
                    <td style={td}>{p.label}</td>
                    <td style={td}>{fmtVal(p.name, v.run)}</td>
                    <td style={{ ...td, color: v.run !== v.cfg ? '#c2410c' : td.color }}>{fmtVal(p.name, v.cfg)}</td>
                    <td style={td}>
                      {active ? fmtVal(p.name, active.snapshot?.[p.name])
                        : profile === 'custom'
                          ? <input type="number" value={custom[p.name] ?? ''} placeholder={String(v.cfg ?? '')}
                              onChange={e => setCustom(c => ({ ...c, [p.name]: e.target.value }))}
                              style={{ width: 140, padding: '4px 6px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }} />
                          : <b style={{ color: '#4338ca' }}>{fmtVal(p.name, target[p.name])}</b>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!active && profile !== 'custom' && profiles[profile]?.note && <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>{profiles[profile].note} Max memory is in 2K pages.</div>}
        </div>
      )}

      {!active && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#334155', marginBottom: 10, cursor: 'pointer' }}>
          <input type="checkbox" checked={suspendJobs} onChange={e => setSuspendJobs(e.target.checked)} />
          Suspend released background jobs (runs BTCTRNS1 now; Rollback runs BTCTRNS2)
        </label>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {!active
          ? <button onClick={doApply} disabled={!!busy || !st} style={{ ...btn('#4f46e5'), opacity: busy || !st ? 0.6 : 1 }}><Play size={14} /> {busy === 'apply' ? 'Applying…' : 'Apply Pre-Copy Settings'}</button>
          : <button onClick={doRollback} disabled={!!busy} style={{ ...btn('#b45309'), opacity: busy ? 0.6 : 1 }}><Undo2 size={14} /> {busy === 'rollback' ? 'Rolling back…' : 'Rollback to Original Settings'}</button>}
      </div>

      {msg && (
        <div style={{ marginTop: 12, background: msg.ok ? '#dcfce7' : '#fef3c7', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: msg.ok ? '#166534' : '#92400e' }}>
          <b>{msg.title}</b>
          {msg.failed?.length > 0 && <div>Not applied: {msg.failed.join(', ')}</div>}
          {msg.restart?.length > 0 && <div>Restart ASE {sid} to activate: {msg.restart.join(', ')}</div>}
          {msg.output && <details style={{ marginTop: 6 }}><summary style={{ cursor: 'pointer' }}>isql output</summary>
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: 11, maxHeight: 240, overflow: 'auto', background: '#fff', padding: 8, borderRadius: 6, color: '#334155' }}>{msg.output}</pre></details>}
        </div>
      )}

      {st?.history?.length > 0 && (
        <details style={{ marginTop: 12 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13, color: '#475569', fontWeight: 600 }}>History ({st.history.length})</summary>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 6 }}>
            <thead><tr>{['Applied', 'By', 'Profile', 'Status', 'Rolled back', 'By'].map((h, i) => <th key={i} style={th}>{h}</th>)}</tr></thead>
            <tbody>{st.history.map(h => (
              <tr key={h.id}>
                <td style={td}>{h.applied_at?.slice(0, 16).replace('T', ' ')}</td><td style={td}>{h.applied_by}</td>
                <td style={td}>{h.profile}</td><td style={td}>{h.status}</td>
                <td style={td}>{h.rolled_back_at ? h.rolled_back_at.slice(0, 16).replace('T', ' ') : '—'}</td><td style={td}>{h.rolled_back_by || '—'}</td>
              </tr>))}</tbody>
          </table>
        </details>
      )}
    </div>
  );
}
